import { Card, CardContent, Stack, Typography } from '@mui/material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { palette } from '../theme';
import { formatMetric, formatMonthLabel } from './formatters';
import type { AreaMetric } from '../api/structure';

export function MetricChart({ metric, height = 320, planLabel = 'Plan' }: { metric: AreaMetric; height?: number; planLabel?: string }) {
  const unit = metric.unit;
  const currentMonth = new Date().toISOString().slice(0, 7);
  // The in-progress month is drawn as its own dashed month-to-date segment. The value is
  // never projected to a full month — extrapolating a partial month produced wildly
  // inflated numbers, especially for ratio metrics.
  const mtdMonth =
    metric.monthToDateActual &&
    metric.series.some((p) => p.month === currentMonth && p.actual != null)
      ? currentMonth
      : null;

  const data = metric.series.map((p) => {
    const isIncomplete = p.month >= currentMonth;
    return {
      month: formatMonthLabel(p.month),
      actual: isIncomplete ? null : p.actual,
      // Drawn as its own dashed segment so a part-month never reads as a closed one
      actualMtd: p.month === mtdMonth ? p.actual : null,
      plan: p.plan,
      // Growth % compares to a prior full month — not meaningful for a partial month
      growthPct: isIncomplete ? null : p.growthPct,
    };
  });

  // Bridge the dashed segment back to the last completed month it grew out of
  const mtdIdx = mtdMonth ? metric.series.findIndex((p) => p.month === mtdMonth) : -1;
  if (mtdIdx > 0) data[mtdIdx - 1].actualMtd = data[mtdIdx - 1].actual;

  const hasNegativeGrowth = data.some((d) => d.growthPct != null && d.growthPct < 0);

  const subtitle = [metric.subarea, metric.source].filter(Boolean).join(' · ');

  return (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {metric.metric}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={data} margin={{ top: 24, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={palette.neutral.border} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: palette.neutral.textMuted }}
              axisLine={false}
              tickLine={false}
              minTickGap={16}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12, fill: palette.neutral.textMuted }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => formatMetric(v, unit, { compact: true })}
              width={56}
            />
            {metric.hasGrowthPct && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12, fill: palette.neutral.textMuted }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => formatMetric(v, 'percent')}
                width={52}
                // Default domain is [0, auto], which clips months with negative growth
                domain={[(dataMin: number) => Math.min(0, dataMin), 'auto']}
              />
            )}
            <Tooltip
              cursor={{ stroke: palette.sage[300], strokeWidth: 1 }}
              contentStyle={{
                background: '#FFFFFF',
                border: `1px solid ${palette.neutral.border}`,
                borderRadius: 10,
                padding: '8px 12px',
                fontSize: 12,
                boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
              }}
              formatter={(value, name) => {
                if (value === null || value === undefined) return ['—', name as string];
                const num = typeof value === 'number' ? value : Number(value);
                if (!Number.isFinite(num)) return ['—', name as string];
                return [formatMetric(num, name === 'Growth %' ? 'percent' : unit), name as string];
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {metric.hasGrowthPct && hasNegativeGrowth && (
              <ReferenceLine yAxisId="right" y={0} stroke={palette.neutral.border} />
            )}
            {metric.hasGrowthPct && (
              <Bar
                yAxisId="right"
                dataKey="growthPct"
                name="Growth %"
                fill={palette.coral[300]}
                radius={[4, 4, 0, 0]}
                maxBarSize={28}
                isAnimationActive={false}
              />
            )}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="actual"
              name="Actual"
              stroke={palette.sage[600]}
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            {mtdMonth && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="actualMtd"
                name="MTD (partial)"
                stroke={palette.sage[600]}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={{ r: 3, fill: palette.sage[600], strokeWidth: 0 }}
                isAnimationActive={false}
              />
            )}
            {metric.hasPlan && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="plan"
                name={planLabel}
                stroke={palette.sage[300]}
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
